import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EventStatusChip from "../events/EventStatusChip";
import {
  openSourceDetailDialog,
  selectSourceChannels,
  selectSourceChannelsSort,
  selectSourceStatusUpdates,
  setSourceSort,
  updateSourceChannelStatus,
} from "../../store/slices/sourceChannelsSlice";

const columns = [
  { id: "id", label: "ID", width: 90 },
  { id: "name", label: "Name", minWidth: 220 },
  { id: "status", label: "Status", width: 140 },
];

const SourceChannelTable = ({ dense = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const rows = useSelector(selectSourceChannels);
  const sort = useSelector(selectSourceChannelsSort);
  const statusUpdates = useSelector(selectSourceStatusUpdates);
  const [confirmAction, setConfirmAction] = useState(null);
  const headerGradient =
    "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)";

  const handleSort = (columnId) => {
    const isAsc =
      sort.field === columnId && (!sort.direction || sort.direction === "asc");
    dispatch(
      setSourceSort({
        field: columnId,
        direction: isAsc ? "desc" : "asc",
      })
    );
  };

  const renderCellValue = (row, column) => {
    const value = row[column.id];
    if (column.id === "status") {
      return <EventStatusChip status={value} />;
    }
    return value || "-";
  };

  return (
    <>
      <TableContainer
        sx={{
          display: "inline-block",
          minWidth: "100%",
          width: "100%",
        }}
      >
        <Table
          size={dense ? "small" : "medium"}
          sx={{ minWidth: 760, borderCollapse: "separate", borderSpacing: 0 }}
        >
          <TableHead
            sx={{
              background: headerGradient,
              "& th": {
                borderBottom: "none",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.3,
                color: theme.palette.common.white,
                "& .MuiTableSortLabel-icon": {
                  color: `${theme.palette.common.white} !important`,
                },
                "& .MuiTableSortLabel-root": {
                  color: "inherit",
                  "&.Mui-active": {
                    color: theme.palette.common.white,
                  },
                },
              },
              "& th:first-of-type": {
                borderTopLeftRadius: 10,
              },
              "& th:last-of-type": {
                borderTopRightRadius: 10,
              },
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={sort.field === column.id ? sort.direction : false}
                  sx={{ minWidth: column.minWidth, width: column.width }}
                >
                  <TableSortLabel
                    active={sort.field === column.id}
                    direction={sort.field === column.id ? sort.direction : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell width={140} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                hover
                tabIndex={-1}
                key={row.id}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(15, 23, 42, 0.02)",
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={`${row.id}-${column.id}`}>
                    {renderCellValue(row, column)}
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View details">
                      <IconButton onClick={() => dispatch(openSourceDetailDialog(row.id))}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit source channel">
                      <IconButton onClick={() => navigate(`/source-channels/${row.id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        row.status === "ACTIVE"
                          ? "Set as inactive"
                          : "Set as active"
                      }
                    >
                      <span>
                        <IconButton
                          color={row.status === "ACTIVE" ? "success" : "default"}
                          onClick={() =>
                            setConfirmAction({
                              id: row.id,
                              nextStatus:
                                row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                              label:
                                row.status === "ACTIVE" ? "inactive" : "active",
                            })
                          }
                          disabled={statusUpdates[row.id] === "loading"}
                        >
                          {row.status === "ACTIVE" ? (
                            <ToggleOnIcon fontSize="small" />
                          ) : (
                            <ToggleOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No source channels match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction
              ? `Set source channel #${confirmAction.id} as ${confirmAction.label}?`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmAction) {
                dispatch(
                  updateSourceChannelStatus({
                    id: confirmAction.id,
                    status: confirmAction.nextStatus,
                  })
                );
              }
              setConfirmAction(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SourceChannelTable;
